import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';
import { isArray, isEmpty } from 'lodash';
import InputMask from 'react-input-mask';
import { capitalCase } from 'change-case';
import { getExtensionArrayByURLs, getReferenceId, phoneFormat } from 'src/utils/fhir';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Add, Check, Edit, Info } from '@mui/icons-material';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import {
  RelatedPersonWrapper,
  WrappedRelatedPerson,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/RelatedPerson';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import AddAddressLoading from 'src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/AddAddressLoading';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { useRelatedPersons, useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { capitalize } from 'src/utils/string';
import uuidv4 from 'src/utils/uuidv4';
import { convertValueToValueSetAll } from 'src/careflow/common/common-utils';
import GenericSearchMemberModal from 'src/careflow/care-coordination/case/detail/components/SearchMember/GenericSearchMemberModal';
import { workflowInstanceData } from 'src/careflow/ph-core/workflow/helpers/workflow';

type Props = {
  patient: WrappedPatient | null;
  isOpen: boolean;
  checklistItem?: any;
  handlerIsOpen: Function;
  workflowInstance: any;
  handleRelatedPerson: (data?: any) => Promise<any>;
};

export default function ConfirmGuarantorInfo({
  patient,
  isOpen,
  checklistItem,
  handlerIsOpen,
  workflowInstance,
  handleRelatedPerson,
}: Props) {
  const { i18n } = useLocales();
  const { componentsData } = useTenantConfigData();
  const { workflow, relatedPerson: relatedPersonConfig } = componentsData;

  const guarantorId = useMemo(() => {
    const dataScope = workflowInstanceData(
      workflowInstance,
      `checklistItem.${checklistItem.code}.modifiedFields.fhirReference`
    );
    const dataScopeReference = Array.isArray(dataScope) ? dataScope?.[0] : dataScope;

    const reference = dataScope ? getReferenceId(dataScopeReference)?.split?.('|')?.[0] : null;
    return reference;
  }, [workflowInstance, checklistItem]);

  const relationshipCodeC: string | null = useMemo(() => {
    if (!checklistItem) {
      return null;
    }

    try {
      const metadata = JSON.parse(checklistItem.metadata);
      const { relationshipCode } = metadata.ui.data;

      if (typeof relationshipCode === 'string') {
        return relationshipCode;
      }
    } catch (err) {
      return null;
    }

    return null;
  }, [checklistItem]);

  const [
    relatedPersons,
    { create: createRelatedPerson, update: updateRelatedPerson, refresh: refreshRelatedPerson },
  ] = useRelatedPersons({
    filter: {
      patient: patient?.id,
      active: true,
      ...(relationshipCodeC ? { relationship: relationshipCodeC } : {}),
    },
    autofetch: !!patient && !!isOpen,
    map: RelatedPersonWrapper,
  });

  const relatedPerson: WrappedRelatedPerson | undefined = useMemo(() => {
    if (guarantorId) return relatedPersons.find((e) => e.id === guarantorId);
    if (relatedPersons.length === 1) return relatedPersons?.[0];
    return undefined;
  }, [relatedPersons, guarantorId]);

  const [housingStatus] = useValueSet('udsplus-housing-status-codes', { map: ValueSetWrapper });
  const [relationshipValueSet] = useValueSet('relatedperson-relationshiptype', {
    map: ValueSetWrapper,
  });

  const [editMode, setEditMode] = useState(false);
  const [guarantorLoaded, setGuarantorLoaded] = useState<boolean>(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [openAddAddress, setOpenAddAddress] = useState(false);
  const [address, setAddress] = useState<any>(null);
  const [externalError, setExternalError] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const [{ resourceRelatedPerson, isSearchMemberOpen }, setSelectedRelatedPerson] = useState<{
    resourceRelatedPerson?: any;
    isSearchMemberOpen?: boolean;
  }>({
    resourceRelatedPerson: null,
    isSearchMemberOpen: false,
  });

  const isAllowedAddressHousingStatus = checkAclValidation({
    acls: [crsAcls.CRS.PATIENT.RELATED_PERSONS.ADD_HOUSING_STATUS],
  });

  const MINIMUM_AGE_TO_SKIP = 18;

  function getMemberAge(dob: string): any {
    const convertDOB = new Date(dob);
    const dateNow = new Date();

    const age = dateNow.getFullYear() - convertDOB.getFullYear();

    return age;
  }

  const schema = Yup.object().shape({
    given: Yup.string()
      .required('First Name is required')
      .matches(/^[a-zA-Z\s]+$/, 'First Name cannot contain numbers'),
    middle_name: Yup.string()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .matches(/^[a-zA-Z\s]+$/, 'Middle Name cannot contain numbers'),
    family: Yup.string()
      .required('Last Name is required')
      .matches(/^[a-zA-Z\s-]+$/, 'Last Name cannot contain numbers'),
    gender: Yup.string().required(`
      ${i18n('patientEngagement.details.gender', 'engagement')} 
      is required`),
    emailType: Yup.string()
      .nullable()
      .transform((curr, orig) => (orig === false ? undefined : curr))
      .when('emailAddress', (value, schema) => {
        if (value) return schema.required('Email Type is required');
      }),
    emailAddress: Yup.string().email('Email must be a valid email address'),
    phoneNumber: Yup.string()
      .nullable()
      .notRequired()
      .transform((value) => {
        if (!value) return null;
        const trimmed = value.trim();
        if (trimmed.length === 0) return null;
        const digitsOnly = trimmed.replace(/\D/g, '');
        return digitsOnly.length === 0 ? null : trimmed;
      })
      .test('test-phone', 'Please enter a valid phone number', function (value) {
        if (!value) return true;
        return /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(value);
      }),
    phoneType: Yup.string()
      .nullable()
      .notRequired()
      .when('phoneNumber', {
        is: (phone) => {
          if (!phone) return false;
          const digitsOnly = phone.replace(/\D/g, '');
          return digitsOnly.length > 0 && /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(phone);
        },
        then: Yup.string().required('Phone Use is required'),
      }),
    relationshipCode: Yup.array()
      .of(
        Yup.object({
          code: Yup.string()
            .nullable()
            .transform((value) => (value === '' ? null : value))
            .required('Relationship is required'),
        })
      )
      .when([], {
        is: () => Boolean(relationshipCodeC),
        then: (schema) => schema.min(1, 'At least one relationship is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    address_use: Yup.string(),
    address_line: Yup.string()
      .nullable()
      .when([], {
        is: () => workflow?.confirmGuarantor?.requiredAddress,
        then: (schema) => schema.required('Street is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    address_line_2: Yup.string(),
    address_city: Yup.string()
      .nullable()
      .when([], {
        is: () => workflow?.confirmGuarantor?.requiredAddress,
        then: (schema) => schema.required('City is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    address_state: Yup.string()
      .nullable()
      .when([], {
        is: () => workflow?.confirmGuarantor?.requiredAddress,
        then: (schema) => schema.required('State is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    address_postal: Yup.string()
      .nullable()
      .when([], {
        is: () => workflow?.confirmGuarantor?.requiredAddress,
        then: (schema) => schema.required('Zip Code is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    address_housing_status: Yup.string()
      .nullable()
      .when([], {
        is: () =>
          relatedPersonConfig?.address?.requiredHousingStatus && isAllowedAddressHousingStatus,
        then: (schema) => schema.required('Housing Status is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
  const mapping = useCallback(
    (resource) =>
      ({
        id: resource?.id || '',
        given: resource?.name?.[0]?.given?.[0] || '',
        middle_name: resource?.name?.[0]?.given?.[1] || '',
        family: resource?.name?.[0]?.family || '',
        gender: resource?.gender || '',
        emailType: resource?.getPrimaryEmail()?.use || '',
        emailAddress: resource?.getPrimaryEmail()?.value || '',
        phoneType: resource?.getPrimaryPhone()?.use || '',
        phoneNumber: resource?.getPrimaryPhone()?.value || '',
        relationshipCode: resource?.relationship?.map((e) => e?.coding?.[0]) ?? [],
        language: {
          language:
            resource?.communication?.[0]?.language?.coding?.[0]?.display ||
            resource?.communication?.[0]?.language?.text ||
            '',
          isDefault: resource?.communication?.[0]?.preferred || false,
        },
        address_use: resource?.getPrimaryAddress()?.use || '',
        address_line: resource?.getPrimaryAddress()?.line?.[0] || '',
        address_line_2: resource?.getPrimaryAddress()?.line?.[1] || '',
        address_city: resource?.getPrimaryAddress()?.city || '',
        address_state: resource?.getPrimaryAddress()?.state || '',
        address_postal: resource?.getPrimaryAddress()?.postalCode || '',
        address_district: resource?.getPrimaryAddress()?.district || '',
        address_housing_status:
          getExtensionArrayByURLs(resource?.address?.[0], [
            'http://fhir.org/guides/hrsa/uds-plus/CodeSystem/udsplus-housing-status-codes',
          ])?.[0]?.valueCode ??
          relatedPersonConfig?.address?.defaultHousingStatus ??
          '',
      } as any),
    [relatedPersonConfig?.address?.defaultHousingStatus]
  );

  const codesRelationship = useMemo(() => {
    if (!relationshipCodeC) return null;

    const codes = Array.isArray(relationshipCodeC)
      ? relationshipCodeC.map((c: any) => String(c).toLowerCase())
      : String(relationshipCodeC)
          .split(',')
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
    return codes;
  }, [relationshipCodeC]);

  const defaultValues = useMemo(() => mapping(relatedPerson), [relatedPerson, mapping]);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const {
    control,
    watch,
    reset,
    setValue,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = methods;

  const values = watch();

  useEffect(() => {
    setGuarantorLoaded(false);
    reset(defaultValues);
    setEditMode(false);
    setGuarantorLoaded(true);
    setExternalError(false);
  }, [isOpen, defaultValues, reset]);

  useEffect(() => {
    reset(mapping(resourceRelatedPerson));
  }, [resourceRelatedPerson, reset, mapping]);

  const saveChanges = async (data: any) => {
    if (
      relatedPersonConfig?.address?.requiredHousingStatus &&
      isAllowedAddressHousingStatus &&
      isEmpty(values?.address_housing_status)
    ) {
      setExternalError(true);
      return;
    }

    setValue('address_line', data.line[0]);
    setValue('address_line_2', data.line[1]);
    setValue('address_city', data.city);
    setValue('address_district', data.district);
    setValue('address_postal', data.postalCode);
    setValue('address_state', data.state);
    setValue('address_use', data.use);
    clearErrors([
      'address_line',
      'address_city',
      'address_postal',
      'address_state',
      'address_housing_status',
    ]);
    setAddress(data);
    setOpenAddAddress(false);
    setExternalError(false);
  };

  const handlerSkip = () => {
    setOpenBackdrop(true);
    handleRelatedPerson?.({ skip: true });
    reset(defaultValues);
    setOpenBackdrop(false);
    handlerIsOpen(false);
    setExternalError(false);
  };

  const onSubmit = async (values: any) => {
    setOpenBackdrop(true);

    const email: any = {
      rank: 1,
      use: values?.emailType,
      system: 'email',
      value: values?.emailAddress,
    };

    const phone: any = {
      rank: 1,
      use: values?.phoneType,
      system: 'phone',
      value: values?.phoneNumber,
    };

    const gender: any = values?.gender;

    let getAddress: any;

    if (!isEmpty(address)) {
      if (isEmpty(address?.extension)) {
        address.extension = [];
      }

      if (values.address_housing_status) {
        const housingStatusExtensionIndex = relatedPerson?.address?.[0]?.extension?.findIndex(
          (item) => item?.url === housingStatus?.getConceptSystem()
        );
        const housingStatusExtension = {
          url: housingStatus?.getConceptSystem(),
          valueCode: values.address_housing_status,
        };
        if (housingStatusExtensionIndex !== undefined && housingStatusExtensionIndex !== -1) {
          address.extension[housingStatusExtensionIndex] = housingStatusExtension;
        } else {
          address.extension.push(housingStatusExtension);
        }
      }
      getAddress = address;
    } else {
      getAddress = relatedPerson?.address;
    }

    const relationship = values?.relationshipCode?.map((e) => ({
      coding: [
        {
          ...convertValueToValueSetAll(e?.code, relationshipValueSet),
        },
      ],
      text: convertValueToValueSetAll(e?.code, relationshipValueSet)?.display,
    }));

    const payload = {
      // Add id to object only of it's in data(updating an existing related person)
      ...(values?.id && { id: values?.id }),
      resourceType: 'RelatedPerson',
      active: relatedPerson?.active !== undefined ? relatedPerson?.active : true,
      name: [
        {
          // add family property to name only if family exists and is not null
          ...(values?.family && {
            family: values?.family,
          }),
          // add given property to name only if given exists and is not null
          ...(values?.given && {
            given: [values?.given, ...(!isEmpty(values?.middle_name) ? [values?.middle_name] : [])],
          }),
        },
      ],
      // add telecom only if phone or email exist
      ...((phone?.value || email?.value) && {
        telecom: [...(phone?.value ? [phone] : []), ...(email?.value ? [email] : [])],
      }),
      ...(getAddress && { address: isArray(getAddress) ? getAddress : [getAddress] }),
      ...(gender && { gender: gender }),
      ...(relationship?.length > 0 ? { relationship: relationship } : null),
      patient: { reference: `${patient?.resourceType}/${patient?.id}` },
    };

    let result: any = null;
    if (payload?.id) {
      result = await updateRelatedPerson(payload);
    } else {
      result = await createRelatedPerson(payload);
    }

    await refreshRelatedPerson();

    handleRelatedPerson?.(result?.[0]);

    reset(defaultValues);
    setOpenBackdrop(false);
    handlerIsOpen(false);
  };

  const handleCloseAlert = () => setOpenAlert(false);

  const handleAgreeAlert = () => {
    setOpenAlert(false);
    handlerIsOpen(false);
  };

  const addressExternal = useMemo(
    () => ({
      id: relatedPerson?.getPrimaryAddress()?.id || '',
      line: relatedPerson?.getPrimaryAddress()?.line || '',
      city: relatedPerson?.getPrimaryAddress()?.city || '',
      district: relatedPerson?.getPrimaryAddress()?.city || '',
      state: relatedPerson?.getPrimaryAddress()?.state || '',
      postalCode: relatedPerson?.getPrimaryAddress()?.postalCode || '',
      country: relatedPerson?.getPrimaryAddress()?.country || '',
      use: relatedPerson?.getPrimaryAddress()?.use || '',
      extension: relatedPerson?.getPrimaryAddress()?.extension || '',
    }),
    [relatedPerson]
  );

  const handlerClose = () => {
    setOpenAddAddress(false);
    setExternalError(false);
  };

  const skipButton = useMemo(
    () =>
      getMemberAge(patient?.birthDate || '') >= MINIMUM_AGE_TO_SKIP ||
      workflow?.confirmGuarantor?.skipAvailable,
    [patient, workflow]
  );

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={() => {
        handlerIsOpen(false);
      }}
    >
      {guarantorLoaded ? (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={openBackdrop}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <List component="nav">
            <ListItem>
              <Grid container justifyContent="space-between">
                <Stack direction="row" spacing={2}>
                  <Button variant="contained" type="submit">
                    {i18n('referral.details.confirm', 'crs')}
                  </Button>
                  <Button onClick={() => setOpenAlert(true)}>
                    {i18n('cancel', 'translations')}
                  </Button>
                </Stack>
                <Grid>
                  <IconButton onClick={() => setEditMode((prev) => !prev)}>
                    {editMode ? <Check /> : <Edit />}
                  </IconButton>
                </Grid>
              </Grid>
            </ListItem>
            <Divider />
            <ListItem>
              <Grid container direction="column" marginTop={2} width={800}>
                {!editMode && skipButton && (
                  <Grid container direction="row" alignItems="center" minHeight={60}>
                    <Grid container item xs={12}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Info htmlColor="#ff9800" sx={{ marginRight: 1.5 }} />
                        <Typography sx={{ marginRight: 1.5 }}>
                          {getMemberAge(patient?.birthDate || '') >= MINIMUM_AGE_TO_SKIP
                            ? i18n(
                                'patientEngagement.details.workflow.checklist.confirmGuarantor.skipButtonAge',
                                'engagement'
                              )
                            : i18n(
                                'patientEngagement.details.workflow.checklist.confirmGuarantor.skipButtonDefault',
                                'engagement'
                              )}
                        </Typography>
                        <Button variant="contained" color="error" onClick={handlerSkip}>
                          {i18n(
                            'patientEngagement.details.workflow.checklist.confirmGuarantor.skip',
                            'engagement'
                          )}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {editMode && relatedPersons?.length > 1 && (
                  <Grid container justifyContent="flex-end">
                    <Stack direction="row" spacing={2} mb={2}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() =>
                          setSelectedRelatedPerson({
                            isSearchMemberOpen: true,
                          })
                        }
                      >
                        {i18n(
                          'patientEngagement.details.workflow.checklist.confirmGuarantor.selectedRelatedPerson',
                          'engagement'
                        )}
                      </Button>
                    </Stack>
                  </Grid>
                )}

                <Grid container direction="row" alignItems="center" minHeight={60}>
                  <Grid container item xs={5}>
                    <Typography>
                      {i18n(
                        'patientEngagement.details.workflow.checklist.confirmGuarantor.name',
                        'engagement'
                      )}{' '}
                      *
                    </Typography>
                  </Grid>
                  {/* ADD '*' to required field */}
                  <Grid container item xs={7}>
                    {editMode ? (
                      <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
                        <Grid item xs={12}>
                          <RHFTextField
                            name="given"
                            label={`${i18n(
                              'patientEngagement.details.workflow.checklist.confirmGuarantor.firstName',
                              'engagement'
                            )} *`}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <RHFTextField
                            name="middle_name"
                            label={i18n(
                              'patientEngagement.details.workflow.checklist.confirmGuarantor.middleName',
                              'engagement'
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <RHFTextField
                            name="family"
                            label={`${i18n(
                              'patientEngagement.details.workflow.checklist.confirmGuarantor.family',
                              'engagement'
                            )} *`}
                          />
                        </Grid>
                      </Stack>
                    ) : (
                      <Stack direction="column">
                        <Typography>
                          <b>{`${values.given} ${values.middle_name} ${values.family}`}</b>
                        </Typography>

                        {!!errors.given && (
                          <FormHelperText error> {errors.given?.message}</FormHelperText>
                        )}

                        {!!errors.middle_name && (
                          <FormHelperText error> {errors.middle_name?.message}</FormHelperText>
                        )}

                        {!!errors.family && (
                          <FormHelperText error> {errors.family?.message}</FormHelperText>
                        )}
                      </Stack>
                    )}
                  </Grid>
                </Grid>

                <Grid container direction="row" alignItems="center" minHeight={60} sx={{ mt: 1 }}>
                  <Grid container item xs={5}>
                    <Typography>
                      {i18n('patientEngagement.details.gender', 'engagement')} *
                    </Typography>
                  </Grid>

                  <Grid container item xs={7}>
                    {editMode ? (
                      <RHFSelect
                        name="gender"
                        label={`${i18n('patientEngagement.details.gender', 'engagement')} *`}
                        fullWidth={true}
                      >
                        <MenuItem disabled />
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                        <MenuItem value="unknown">Unknown</MenuItem>
                      </RHFSelect>
                    ) : (
                      <Stack direction="column">
                        <Typography>
                          <b>{capitalCase(values.gender)}</b>
                        </Typography>

                        {!!errors.gender && (
                          <FormHelperText error> {errors.gender?.message}</FormHelperText>
                        )}
                      </Stack>
                    )}
                  </Grid>
                </Grid>

                <Grid container direction="row" alignItems="center" minHeight={60} sx={{ mt: 1 }}>
                  <Grid container item xs={5}>
                    <Typography>
                      {`${i18n('patients.details.contacts.relationship', 'crs')}${
                        relationshipCodeC ? ' *' : ''
                      }`}
                    </Typography>
                  </Grid>
                  <Grid container item xs={7}>
                    {editMode && relationshipValueSet?.asListAll() ? (
                      <Controller
                        name="relationshipCode"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            multiple
                            freeSolo
                            fullWidth
                            onChange={(event, newValue) => field.onChange(newValue)}
                            options={
                              codesRelationship
                                ? relationshipValueSet
                                    ?.asListAll()
                                    ?.filter((item) =>
                                      codesRelationship.includes(
                                        String(item?.code || '').toLowerCase()
                                      )
                                    )
                                : relationshipValueSet?.asListAll()
                            }
                            getOptionLabel={(option) => option?.display}
                            isOptionEqualToValue={(option, value) => option.code === value.code}
                            filterOptions={(options, params) =>
                              options?.filter((item) =>
                                item?.display
                                  ?.toLowerCase()
                                  .includes(params?.inputValue?.toLowerCase())
                              )
                            }
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  {...getTagProps({ index })}
                                  key={option?.code}
                                  size="small"
                                  label={option?.display}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                label={`${i18n('patients.details.contacts.relationship', 'crs')}${
                                  relationshipCodeC ? ' *' : ''
                                }`}
                                {...params}
                                error={!!errors.relationshipCode}
                                helperText={errors.relationshipCode?.message}
                              />
                            )}
                          />
                        )}
                      />
                    ) : (
                      <Stack direction="column">
                        <Typography>
                          {!isEmpty(values?.relationshipCode) &&
                            values?.relationshipCode?.map((e) => (
                              <Chip
                                clickable={false}
                                size="small"
                                key={uuidv4()}
                                label={e?.display}
                                sx={{ m: 0.2, textTransform: 'capitalize' }}
                              />
                            ))}
                        </Typography>

                        {!!errors.relationshipCode && (
                          <FormHelperText error> {errors.relationshipCode?.message}</FormHelperText>
                        )}
                      </Stack>
                    )}
                  </Grid>
                </Grid>

                <Grid container direction="row" alignItems="center" minHeight={60} sx={{ mt: 1 }}>
                  <Grid container item xs={5}>
                    <Typography>{i18n('patients.email', 'crs')}</Typography>
                  </Grid>

                  <Grid container item xs={7}>
                    {editMode ? (
                      <>
                        <Grid item xs={3}>
                          <RHFSelect
                            name="emailType"
                            label={i18n('patients.details.relatedPersons.emailUse', 'crs')}
                            fullWidth={true}
                          >
                            <MenuItem disabled />
                            <MenuItem value="home">Home</MenuItem>
                            <MenuItem value="work">Work</MenuItem>
                          </RHFSelect>
                        </Grid>
                        <Grid item xs={9}>
                          <RHFTextField
                            name="emailAddress"
                            label={i18n('patients.email', 'crs')}
                            fullWidth
                          />
                        </Grid>
                      </>
                    ) : (
                      <Stack direction="column">
                        <Typography>
                          <b>{`${values.emailAddress} (${capitalCase(values.emailType)})`}</b>
                        </Typography>

                        {!!errors.emailAddress && (
                          <FormHelperText error> {errors.emailAddress?.message}</FormHelperText>
                        )}
                        {!!errors.emailType && (
                          <FormHelperText error> {errors.emailType?.message}</FormHelperText>
                        )}
                      </Stack>
                    )}
                  </Grid>
                </Grid>

                <Grid container direction="row" alignItems="center" minHeight={60} sx={{ mt: 1 }}>
                  <Grid container item xs={5}>
                    <Typography>{i18n('patients.phone', 'crs')}</Typography>
                  </Grid>

                  <Grid container item xs={7}>
                    {editMode ? (
                      <>
                        <Grid item xs={3}>
                          <RHFSelect
                            name="phoneType"
                            label={i18n('patients.details.relatedPersons.phoneUse', 'crs')}
                            fullWidth={true}
                          >
                            <MenuItem disabled />
                            <MenuItem value="home">Home</MenuItem>
                            <MenuItem value="work">Work</MenuItem>
                            <MenuItem value="mobile">Mobile</MenuItem>
                          </RHFSelect>
                        </Grid>
                        <Grid item xs={9}>
                          <Controller
                            name="phoneNumber"
                            control={control}
                            render={({
                              field: { onChange, value, onBlur },
                              fieldState: { error, isDirty },
                            }) => {
                              const isPlaceholder = (val: string | null | undefined) => {
                                if (!val) return true;
                                const digitsOnly = val.replace(/\D/g, '');
                                return digitsOnly.length === 0;
                              };

                              const digitsOnly = value ? value.replace(/\D/g, '') : '';
                              const hasValue = digitsOnly.length > 0;
                              const shouldShowError = isDirty && hasValue && !!error?.message;

                              const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                const newValue = e.target.value;
                                if (isPlaceholder(newValue)) {
                                  onChange('');
                                } else {
                                  onChange(newValue);
                                }
                              };

                              return (
                                <InputMask
                                  mask="(999) 999-9999"
                                  value={value || ''}
                                  onChange={handleChange}
                                  onBlur={onBlur}
                                >
                                  {(inputProps: any) => (
                                    <TextField
                                      label={i18n('patients.phone', 'crs')}
                                      {...inputProps}
                                      error={shouldShowError}
                                      helperText={shouldShowError ? error?.message : ''}
                                    />
                                  )}
                                </InputMask>
                              );
                            }}
                          />
                        </Grid>
                      </>
                    ) : (
                      <Stack direction="column">
                        <Typography>
                          <b>{`${phoneFormat(values.phoneNumber)} (${capitalCase(
                            values.phoneType
                          )})`}</b>
                        </Typography>

                        {!!errors.phoneType && (
                          <FormHelperText error> {errors.phoneType?.message}</FormHelperText>
                        )}
                        {!!errors.phoneNumber && (
                          <FormHelperText error> {errors.phoneNumber?.message}</FormHelperText>
                        )}
                      </Stack>
                    )}
                  </Grid>
                </Grid>

                <Grid container direction="row" alignItems="center" minHeight={60}>
                  <Grid container item xs={5}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <Typography>
                        Address {workflow?.confirmGuarantor?.requiredAddress && '*'}
                      </Typography>
                      <Tooltip
                        title={i18n(
                          'patientEngagement.details.workflow.checklist.confirmGuarantor.referLocalPolicy',
                          'engagement'
                        )}
                        placement="top"
                      >
                        <Info htmlColor="#00b8d9" sx={{ marginRight: 1.5 }} />
                      </Tooltip>
                    </Box>
                  </Grid>

                  <Grid container item xs={7}>
                    {editMode ? (
                      <>
                        <Grid item xs={12} marginTop={1} display="flex" justifyContent="flex-end">
                          <Box
                            sx={{
                              paddingY: 2,
                              paddingX: 2,
                              backgroundColor: '#cafdf5',
                              borderRadius: 1,
                              gap: 12,
                            }}
                          >
                            <Box
                              sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                            >
                              <Info htmlColor="#00b8d9" sx={{ marginRight: 1.5 }} />
                              <Typography
                                variant="body2"
                                sx={{ color: '#003768', fontSize: '0.94rem' }}
                              >
                                {i18n(
                                  'patientEngagement.details.workflow.checklist.confirmGuarantor.referLocalPolicy',
                                  'engagement'
                                )}
                              </Typography>
                              <Button
                                color="primary"
                                startIcon={<Add />}
                                variant="contained"
                                sx={{ marginLeft: 1.5 }}
                                onClick={() => setOpenAddAddress(true)}
                              >
                                Add
                              </Button>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={6} marginTop={1}>
                          <RHFTextField name="address_line" label="Street*" disabled />
                        </Grid>
                        <Grid item xs={6} marginTop={1}>
                          <RHFTextField name="address_line_2" label="Street 2" disabled />
                        </Grid>
                        <Grid item xs={6} marginTop={1}>
                          <RHFTextField name="address_city" label="City*" disabled />
                        </Grid>
                        <Grid item xs={6} marginTop={1}>
                          <RHFTextField name="address_state" label="State*" disabled />
                        </Grid>
                        <Grid item xs={6} marginTop={1}>
                          <RHFTextField name="address_district" label="County" disabled />
                        </Grid>
                        <Grid item xs={6} marginTop={1}>
                          <RHFTextField name="address_postal" label="Zip Code*" disabled />
                        </Grid>
                        {isAllowedAddressHousingStatus && (
                          <Grid item xs={6} marginTop={1}>
                            <RHFTextField
                              name="address_housing_status"
                              label={`Housing Status ${
                                relatedPersonConfig?.address?.requiredHousingStatus ? '*' : ''
                              }`}
                              value={
                                values.address_housing_status
                                  ? capitalize(values?.address_housing_status?.replaceAll('-', ' '))
                                  : ''
                              }
                              disabled
                            />
                          </Grid>
                        )}
                      </>
                    ) : (
                      <Grid item xs={9}>
                        <Typography>
                          <b>{`${values.address_line || ''}, ${values.address_line_2 || ''} ${
                            values.address_city || ''
                          }, 
                              ${values.address_state || ''}, ${values.address_district || ''},  ${
                            values.address_postal || ''
                          }
                              ${
                                values.address_housing_status && isAllowedAddressHousingStatus
                                  ? capitalize(values?.address_housing_status?.replaceAll('-', ' '))
                                  : ''
                              }`}</b>
                        </Typography>
                        <Stack>
                          {!!errors.address_line && (
                            <FormHelperText error> {errors.address_line?.message}</FormHelperText>
                          )}

                          {!!errors.address_city && (
                            <FormHelperText error> {errors.address_city?.message}</FormHelperText>
                          )}

                          {!!errors.address_state && (
                            <FormHelperText error> {errors.address_state?.message}</FormHelperText>
                          )}

                          {!!errors.address_postal && (
                            <FormHelperText error> {errors.address_postal?.message}</FormHelperText>
                          )}

                          {!!errors.address_housing_status && !openAddAddress && (
                            <FormHelperText error>
                              {' '}
                              {errors.address_housing_status?.message}
                            </FormHelperText>
                          )}
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <GenericSearchMemberModal
                  patient={patient}
                  isSearchMemberOpen={!!isSearchMemberOpen}
                  handleSave={(resource) =>
                    setSelectedRelatedPerson({
                      resourceRelatedPerson: resource,
                    })
                  }
                  title={i18n('patients.details.relatedPersons.title', 'crs')}
                  resource="RelatedPerson"
                  i18n={i18n}
                  handleClose={() =>
                    setSelectedRelatedPerson({
                      isSearchMemberOpen: false,
                    })
                  }
                  filter={relationshipCodeC ? { relationship: relationshipCodeC } : undefined}
                />
              </Grid>
            </ListItem>
          </List>
        </FormProvider>
      ) : (
        <Stack width={500} alignItems="center" marginTop={50}>
          <CircularProgress />
        </Stack>
      )}

      <Dialog
        open={openAlert}
        onClose={handleCloseAlert}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirm Parent/Caregiver Info'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {i18n('previousChanges', 'translations')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAlert}
            variant="contained"
            color="error"
            sx={{ marginRight: 1 }}
          >
            {i18n('disagree', 'translations')}
          </Button>
          <Button onClick={handleAgreeAlert} autoFocus variant="contained" color="success">
            {i18n('agree', 'translations')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddAddress} fullWidth maxWidth="md">
        <DialogTitle> Add Address</DialogTitle>
        <Card sx={{ p: 2, mx: 2 }}>
          <AddAddressLoading
            isEditable={true}
            externalAddress={addressExternal}
            handleClose={handlerClose}
            handleSave={saveChanges}
            externalError={externalError}
            {...(isAllowedAddressHousingStatus
              ? {
                  childrenTop: (
                    <ListItem>
                      <FormControl fullWidth error={externalError}>
                        <InputLabel id="address-housing-status-label">
                          Housing Status{' '}
                          {relatedPersonConfig?.address?.requiredHousingStatus ? '*' : ''}
                        </InputLabel>
                        <Select
                          labelId="address-housing-status-label"
                          id="addressHousingStatus"
                          name="addressHousingStatus"
                          label={`Housing Status ${
                            relatedPersonConfig?.address?.requiredHousingStatus ? '*' : ''
                          }`}
                          value={values.address_housing_status ?? ''}
                          onChange={(ev) => {
                            setValue('address_housing_status', String(ev.target.value));
                            setExternalError(false);
                          }}
                        >
                          {housingStatus?.asList().map((option) => (
                            <MenuItem key={option.code} value={option.code}>
                              {capitalize(option?.code?.replaceAll('-', ' '))}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </ListItem>
                  ),
                }
              : {})}
          />
        </Card>
      </Dialog>
    </Drawer>
  );
}
